import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Controller, useForm } from 'react-hook-form'
import { Grid } from '@material-ui/core'
import { useAppContext } from '../../../../../redux/slices/appContext'
import { postNamedCommand } from '../../../../../service'
import { QUERY_KEYS } from '../../../../../api/queryKeys'
import RightPanel from '../../../RightPanel'
import Loading from '../../../../molecules/Loading'
import SydButton from '../../../../commonDesign/Button'
import SydLabel from '../../../../commonDesign/SydLabel'
import SydInput from '../../../../commonDesign/SydInput'
import SydSelect from '../../../../commonDesign/SydSelect'
import SydModalActions from '../../../../commonDesign/SydModal/SydModalActions'
import { extractWealthboxErrorMessage } from '../shared/wealthboxErrors'
import { useWealthboxContactDetail } from './useWealthboxContactInfo'

// Field groups for the SMT-1356 visible-contact-fields config. Every `key`
// here is the ACTUAL Wealthbox response/body field name (verified against
// dev.wealthbox.com Aug 2026). The original SMT-1356 ticket used several
// wrong names: `date_of_birth` (real: `birth_date`), `addresses` (real:
// `street_addresses`), `employer` (real: `company_name`), `advisor` (real:
// `assigned_to`, a numeric user id), `source` (real: `contact_source`,
// enum), `category` (real: `contact_type`, enum).
//
// The `kind` field on email/phone/address entries (NOT `type` as it'd be
// natural to assume) carries the Work/Home/Mobile/etc. label.
const ALL_FIELDS = [
  // Identity
  { key: 'first_name', label: 'First Name', group: 'Identity', type: 'text' },
  { key: 'last_name', label: 'Last Name', group: 'Identity', type: 'text' },
  { key: 'nickname', label: 'Nickname', group: 'Identity', type: 'text' },
  { key: 'birth_date', label: 'Date of Birth', group: 'Identity', type: 'date' },
  {
    key: 'gender',
    label: 'Gender',
    group: 'Identity',
    type: 'enum',
    options: ['Female', 'Male', 'Non-binary', 'Unknown']
  },
  // Contact
  { key: 'email_addresses', label: 'Emails', group: 'Contact', type: 'multi', valueKey: 'address' },
  { key: 'phone_numbers', label: 'Phone', group: 'Contact', type: 'multi', valueKey: 'address' },
  { key: 'street_addresses', label: 'Addresses', group: 'Contact', type: 'address-multi' },
  // Professional
  { key: 'company_name', label: 'Company', group: 'Professional', type: 'text' },
  { key: 'job_title', label: 'Job Title', group: 'Professional', type: 'text' },
  // Personal / Classification
  { key: 'assigned_to', label: 'Advisor', group: 'Personal', type: 'user-id' },
  {
    key: 'contact_source',
    label: 'Source',
    group: 'Personal',
    type: 'enum',
    options: ['Referral', 'Conference', 'Direct Mail', 'Cold Call', 'Other']
  },
  {
    key: 'contact_type',
    label: 'Category',
    group: 'Personal',
    type: 'enum',
    options: ['Client', 'Past Client', 'Prospect', 'Vendor', 'Organization']
  },
  {
    key: 'status',
    label: 'Status',
    group: 'Personal',
    type: 'enum',
    options: ['Active', 'Inactive']
  }
]

function formatValue (field, raw) {
  if (raw == null) return null
  if (field.type === 'date') {
    const d = dayjs(raw)
    return d.isValid() ? d.format('MMM D, YYYY') : raw
  }
  if (field.type === 'user-id') {
    // `assigned_to` (the Advisor) comes back as a bare numeric Wealthbox
    // user id. Resolving it to a display name would require a `/v1/users`
    // lookup (cached firm-wide) which is intentionally out of scope for
    // SMT-1356 - product agreed that surfacing the raw id as a placeholder
    // is acceptable for v1 since the read-only field is rarely consulted.
    return `User #${raw}`
  }
  if (field.type === 'enum' || field.type === 'text') {
    return String(raw)
  }
  if (field.type === 'multi') {
    const items = Array.isArray(raw) ? raw : []
    if (!items.length) return null
    return items.map((entry, i) => (
      <div key={i}>
        <span>{entry[field.valueKey]}</span>
        {/* Wealthbox uses `kind` (NOT `type`) for the work/home/mobile
            type-label on email_addresses, phone_numbers, street_addresses. */}
        {entry.kind ? (
          <span style={{ color: 'var(--text-color-subtle)', marginLeft: '8px' }}>({entry.kind})</span>
        ) : null}
      </div>
    ))
  }
  if (field.type === 'address-multi') {
    const items = Array.isArray(raw) ? raw : []
    if (!items.length) return null
    return items.map((addr, i) => {
      const lines = [
        [addr.street_line_1, addr.street_line_2].filter(Boolean).join(' '),
        [addr.city, addr.state, addr.zip_code].filter(Boolean).join(', '),
        addr.country
      ].filter(Boolean)
      return (
        <div key={i} style={{ marginBottom: '4px' }}>
          {lines.map((line, j) => <div key={j}>{line}</div>)}
          {addr.kind ? <span style={{ color: 'var(--text-color-subtle)' }}>({addr.kind})</span> : null}
        </div>
      )
    })
  }
  return String(raw)
}

const labelStyle = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 700,
  textTransform: 'uppercase',
  color: 'var(--text-color-subtle)',
  marginBottom: '4px'
}

function ViewingBody ({ contact, visibleFields }) {
  // Filter ALL_FIELDS to just what the admin configured (or all if unset),
  // then group by section. Empty values render as a dash so the structure
  // is preserved (AC: "field is either hidden or shown with a clear empty/
  // dash state - no raw null or blank label").
  const visible = visibleFields?.length
    ? ALL_FIELDS.filter(f => visibleFields.includes(f.key))
    : ALL_FIELDS

  const grouped = visible.reduce((acc, f) => {
    acc[f.group] = acc[f.group] || []
    acc[f.group].push(f)
    return acc
  }, {})

  return (
    <div>
      {Object.entries(grouped).map(([group, fields]) => (
        <div key={group} style={{ marginBottom: '16px' }}>
          <div style={{ ...labelStyle, borderBottom: '1px solid var(--color-neutral-200)', paddingBottom: '4px', marginBottom: '8px' }}>
            {group}
          </div>
          <Grid container spacing={2}>
            {fields.map(field => {
              const value = formatValue(field, contact?.[field.key])
              return (
                <Grid item xs={12} sm={field.type === 'multi' || field.type === 'address-multi' ? 12 : 6} key={field.key}>
                  <div style={labelStyle}>{field.label}</div>
                  <div style={{ fontSize: 'var(--font-size-sm)' }}>
                    {value || <span style={{ color: 'var(--text-color-subtle)' }}>—</span>}
                  </div>
                </Grid>
              )
            })}
          </Grid>
        </div>
      ))}
    </div>
  )
}
ViewingBody.propTypes = {
  contact: PropTypes.object,
  visibleFields: PropTypes.arrayOf(PropTypes.string)
}

// Edit form. Scalar text/date/enum fields are editable inline; array fields
// (email_addresses, phone_numbers, street_addresses) and user-id refs
// (assigned_to) are read-only for now. Adding structured editors for those
// is filed as a follow-up.
function EditingBody ({ contact, visibleFields, onSubmit, onCancel }) {
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)

  const editableFields = (visibleFields?.length
    ? ALL_FIELDS.filter(f => visibleFields.includes(f.key))
    : ALL_FIELDS
  ).filter(f => f.type === 'text' || f.type === 'date' || f.type === 'enum')

  const defaults = editableFields.reduce((acc, f) => {
    acc[f.key] = contact?.[f.key] ?? ''
    return acc
  }, {})

  const { control, handleSubmit } = useForm({ defaultValues: defaults })

  const submit = handleSubmit(async (values) => {
    setSubmitting(true)
    setSubmitError(null)
    try {
      // Only submit changed fields (matches AC's "submit only changed fields").
      const body = {}
      for (const field of editableFields) {
        const v = values[field.key]
        if (v !== contact?.[field.key]) {
          body[field.key] = v === '' ? null : v
        }
      }
      await onSubmit(body)
    } catch (err) {
      setSubmitError(extractWealthboxErrorMessage(err, 'Failed to save contact'))
    } finally {
      setSubmitting(false)
    }
  })

  return (
    <form onSubmit={submit}>
      <Grid container spacing={2}>
        {editableFields.map(field => (
          <Grid item xs={12} sm={6} key={field.key}>
            <Controller
              name={field.key}
              control={control}
              render={({ field: f }) => (
                <SydLabel label={field.label}>
                  {field.type === 'enum' ? (
                    <SydSelect
                      options={[
                        { label: '(None)', value: '' },
                        ...(field.options || []).map(o => ({ label: o, value: o }))
                      ]}
                      value={f.value || ''}
                      onChange={(e) => f.onChange(e.target.value)}
                    />
                  ) : (
                    <SydInput
                      {...f}
                      type={field.type === 'date' ? 'date' : 'text'}
                      value={f.value || ''}
                      placeholder={field.label}
                    />
                  )}
                </SydLabel>
              )}
            />
          </Grid>
        ))}
      </Grid>

      {submitError ? (
        <div className='font-size-xs mt-2' style={{ color: 'var(--color-decline-500)' }}>{submitError}</div>
      ) : null}

      <SydModalActions>
        <SydButton variant='ghost' size='sm' onClick={onCancel} disabled={submitting} type='button'>Cancel</SydButton>
        <SydButton variant='primary' size='sm' processing={submitting} type='submit'>Save Changes</SydButton>
      </SydModalActions>
    </form>
  )
}
EditingBody.propTypes = {
  contact: PropTypes.object,
  visibleFields: PropTypes.arrayOf(PropTypes.string),
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
}

const MODE_VIEWING = 'viewing'
const MODE_EDITING = 'editing'

const PANEL_INVALIDATE_KEYS = [
  [QUERY_KEYS.wealthboxDomainQuery, 'contact-info-primary'],
  [QUERY_KEYS.wealthboxDomainQuery, 'contact-info-detail'],
  [QUERY_KEYS.wealthboxDomainQuery, 'contact-info-relationships']
]

function WealthboxContactPanel ({ open, onClose, contactId, contactName, enableEdit, visibleFields }) {
  const { clientId } = useAppContext()
  const queryClient = useQueryClient()
  const [mode, setMode] = useState(MODE_VIEWING)

  useEffect(() => { if (open) setMode(MODE_VIEWING) }, [open, contactId])

  const detailQuery = useWealthboxContactDetail(contactId, { enabled: open })

  const { mutateAsync: handleSave } = useMutation({
    mutationFn: (body) => postNamedCommand('coreData', 'execute-wealthbox-domain-command', {
      command: 'modifyContact',
      clientId,
      request: { contactId, body }
    }),
    onSuccess: () => {
      setMode(MODE_VIEWING)
      for (const key of PANEL_INVALIDATE_KEYS) queryClient.invalidateQueries({ queryKey: key })
    }
  })

  return (
    <RightPanel open={open} toggleOpen={onClose} title='Contact Info' showCloseButton>
      <div style={{ padding: '0 1.5rem 1.5rem' }}>
        {detailQuery.isFetching && !detailQuery.data ? (
          <Loading />
        ) : detailQuery.error ? (
          <div className='font-size-sm' style={{ color: 'var(--color-decline-500)', padding: '12px 0' }}>
            Failed to load contact details.
          </div>
        ) : !detailQuery.data ? null : (
          <>
            <div className='font-size-lg' style={{ fontWeight: 600, marginBottom: '12px' }}>
              {contactName || [detailQuery.data.first_name, detailQuery.data.last_name].filter(Boolean).join(' ')}
            </div>

            {mode === MODE_VIEWING && (
              <>
                <ViewingBody contact={detailQuery.data} visibleFields={visibleFields} />
                {enableEdit ? (
                  <SydModalActions>
                    <div style={{ flex: 1 }} />
                    <SydButton variant='outline' size='sm' icon='edit' onClick={() => setMode(MODE_EDITING)}>
                      Edit
                    </SydButton>
                  </SydModalActions>
                ) : null}
              </>
            )}

            {mode === MODE_EDITING && (
              <EditingBody
                contact={detailQuery.data}
                visibleFields={visibleFields}
                onSubmit={handleSave}
                onCancel={() => setMode(MODE_VIEWING)}
              />
            )}
          </>
        )}
      </div>
    </RightPanel>
  )
}

WealthboxContactPanel.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  contactId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  contactName: PropTypes.string,
  enableEdit: PropTypes.bool,
  visibleFields: PropTypes.arrayOf(PropTypes.string)
}

export default WealthboxContactPanel
export { ALL_FIELDS as CONTACT_INFO_FIELDS }
